/**
 * @file css class names for font
 */

import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'

enum FontNameToFontId {
  Inter = 1,
  Lora = 2,
  ComicNeueAngular = 3,
  AverageMono = 4,
  ShantellSans = 5,
  CourierPrime = 6,
}

const LOCALE_FONT_OVERRIDE_LOCALES = [
  'zh-CN',
  'zh',
  'zh-TW',
  'ar',
  'fa',
  'bg',
  'ru',
  'sr',
  'vi',
  'bn',
  'el',
  'he',
  'hi',
  'ja',
  'ko',
  'th',
  'tr',
  'uk',
  'ur',
]

function hasLocaleFontOverride(): boolean {
  const locale = getHyphenatedCurrentLocale()
  return LOCALE_FONT_OVERRIDE_LOCALES.some((l) => locale === l)
}

function tailwindSurfaceFontClass(fontId: FontNameToFontId): string {
  let base: string
  switch (fontId) {
    case FontNameToFontId.Inter:
      base = 'font-sans'
      break
    case FontNameToFontId.Lora:
      base = 'font-serif'
      break
    case FontNameToFontId.ComicNeueAngular:
      base = 'font-cursive'
      break
    case FontNameToFontId.AverageMono:
      base = isAppUsing('surfaceLocaleFontOverride') ? 'font-courier' : 'font-padlet-monospace'
      break
    case FontNameToFontId.ShantellSans:
      base = 'font-shantell'
      break
    case FontNameToFontId.CourierPrime:
      base = 'font-courier'
      break
    default:
      base = 'font-sans'
  }
  return hasLocaleFontOverride() ? `${base} surface-locale-font` : base
}

/**
 * A distinct font style that users can select to apply to texts they create on sandbox.
 * 
 * 04 March 2026: Must be called at runtime (e.g. inside setup(), computed(), or event handlers),
 * not at module scope. Relies on feature flags that are only available after page load.
 *
 * This behavior is intended to be temporary until the sandboxLocaleFontOverride feature is launched.
 * Afterwhich, we should revert back to the original constant declaration.
 * Refer to https://linear.app/padlet/issue/PRO-28829/change-copy-for-fonts.
 */
function getWhiteboardThemeFontOptions(): Record<number, string> {
  return {
    [FontNameToFontId.Inter]: __('Modern'),
    [FontNameToFontId.Lora]: __('Classic'),
    [FontNameToFontId.CourierPrime]: isAppUsing('sandboxLocaleFontOverride') ? __('Distinct') : __('Technical'),
    [FontNameToFontId.ShantellSans]: isAppUsing('sandboxLocaleFontOverride') ? __('Handwriting') : __('Playful'),
  }
}

export { FontNameToFontId, getWhiteboardThemeFontOptions, tailwindSurfaceFontClass }
